import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ColumnTypesStateService } from '@y42/column-type';
import { AbstractListViewService, ListViewData } from '@y42/core/list-view';
import { ColumnTypeEnum } from '@y42/json-schema';
import { BehaviorSubject, map, Observable, switchMap } from 'rxjs';
import { TypescriptTypeToColumnTypeConverter } from '../../helpers/typescript-to-column-type-converter.helper';
import { FunctionDefinitionModel } from '../../models/function-definition.model';
import { MonacoContentfulFunctionsState } from '../../monaco-contentful-functions.state';

type FunctionDefinitionModelListViewData = ListViewData<FunctionDefinitionModel>;

@UntilDestroy()
@Injectable()
export class FunctionListViewService extends AbstractListViewService<FunctionDefinitionModelListViewData> {
  constructor(
    private readonly monacoContentfulFunctionsState: MonacoContentfulFunctionsState,
    private readonly columnTypesStateService: ColumnTypesStateService,
  ) {
    super();
  }

  private readonly showAggregateFunctions$ = new BehaviorSubject(false);

  readonly data$ = this.showAggregateFunctions$.pipe(
    switchMap((show) =>
      this.monacoContentfulFunctionsState.selectMonacoFunctions(show).pipe(
        map((functions) => functions.map((func) => ({ ...func, id: func.function_name }))),
        map((functions) =>
          functions.map((func) => {
            const firstParameter = func.parameters.find((parameter) => parameter.index === 1);
            if (firstParameter) {
              return {
                ...func,
                first_input_type:
                  firstParameter.typescript_type.indexOf('|') !== -1
                    ? ColumnTypeEnum.Dynamic // TODO: used to be multi ???
                    : TypescriptTypeToColumnTypeConverter(firstParameter.typescript_type),
                parameter_length: func.parameters.length,
              };
            } else {
              return {
                ...func,
                first_input_type: ColumnTypeEnum.String, // TODO: used to be null
                parameter_length: 0,
              };
            }
          }),
        ),
      ),
    ),
    untilDestroyed(this),
  );

  readonly loading$: Observable<boolean> = this.monacoContentfulFunctionsState
    .select((s) => s.loading)
    .pipe(untilDestroyed(this));

  readonly columnTypes$ = this.columnTypesStateService.select((s) => s.entityState).pipe(untilDestroyed(this));

  async getAll(): Promise<void> {
    await Promise.all([
      this.monacoContentfulFunctionsState.refreshContentfulEntries(),
      this.columnTypesStateService.refreshContentfulEntries(),
    ]);
  }

  public setShowAggregateFunctions(show: boolean) {
    this.showAggregateFunctions$.next(show);
  }

  public getTypeDefinition(type: ColumnTypeEnum) {
    return this.columnTypesStateService.getTypeById(type);
  }
}
